"use client";

import { useMemo } from "react";
import useSWR from "swr";
import { useGraphql } from "~/api-client";
import { User } from "~/types";

interface HookResult {
  user: User | null;
  refetch: () => Promise<User | null>;
  error: unknown;
  isValidating: boolean;
  isLoading: boolean;
}

export function useUser(options?: { revalidateOnFocus?: boolean }): HookResult {
  const { getCurrentUser } = useGraphql();
  const { data, mutate, error, isValidating, isLoading } = useSWR(
    "user",
    getCurrentUser,
    options,
  );

  return useMemo(() => {
    const user: User | null =
      data && data.me
        ? {
            isPaymentsVerified: data.me.isPaymentsVerified,
            firstName: data.me.firstName,
            lastName: data.me.lastName,
            dob: data.me.dob || null,
            emailAddress: data.me.emailAddress || null,
            phoneNumber: data.me.phoneNumber || null,
            hasVerifiedSession: !data.session?.verified ? false : true,
            hasAgreedToTerms: data.me.hasAgreedToTerms,
          }
        : null;

    const refetch = async () => {
      const data = await mutate();
      const user: User | null =
        data && data.me
          ? {
              isPaymentsVerified: data.me.isPaymentsVerified,
              firstName: data.me.firstName,
              lastName: data.me.lastName,
              dob: data.me.dob || null,
              emailAddress: data.me.emailAddress || null,
              phoneNumber: data.me.phoneNumber || null,
              hasVerifiedSession: !data.session?.verified ? false : true,
              hasAgreedToTerms: data.me.hasAgreedToTerms,
            }
          : null;
      return user;
    };

    return {
      user,
      refetch,
      error,
      isValidating,
      isLoading,
    };
  }, [data, error, isLoading, isValidating, mutate]);
}
